// src/components/Organizer/ProfileManagement.tsx
import React, { useState, useEffect, useRef } from "react";
import { IconType } from "react-icons";
import { useOrganizer } from "../../contexts/OrganizerContext";
import { apiClient } from "../../utils/api";
import { toast } from "react-toastify";
import {
	SOCIAL_PLATFORMS,
	SOCIAL_PLATFORM_ICONS,
	SOCIAL_PLATFORM_MAP,
	createEmptySocialLinkState,
	SocialLinkState,
	SocialPlatformKey,
} from "../../constants/socialPlatforms";

const ProfileManagement: React.FC = () => {
	const { organizer, updateOrganizerProfile, refreshOrganizerData } =
		useOrganizer();
	const [isEditing, setIsEditing] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isUploadingLogo, setIsUploadingLogo] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [formData, setFormData] = useState({
		organizerName: organizer?.organizer_name || "",
		representativeName: organizer?.representative_name || "",
		email: organizer?.email || "",
		phone: organizer?.phone || "",
		website: organizer?.website || "",
		description: organizer?.description || "",
		logoUrl: organizer?.logo_url || "https://via.placeholder.com/150",
	});
	const [socialLinks, setSocialLinks] = useState<SocialLinkState>(
		createEmptySocialLinkState()
	);
	const socialLinkBadges = (organizer?.social_links || [])
		.map((link) => {
			const platformKey = link.platform as SocialPlatformKey;
			const Icon = SOCIAL_PLATFORM_ICONS[platformKey];
			const meta = SOCIAL_PLATFORM_MAP[platformKey];
			if (!meta || !link.url || !Icon) {
				return null;
			}
			return {
				key: platformKey,
				url: link.url,
				label: meta.label,
				Icon,
			};
		})
		.filter(
			(
				link
			): link is {
				key: SocialPlatformKey;
				url: string;
				label: string;
				Icon: IconType;
			} => link !== null
		);

	// 主催者データの変更時にフォームを更新
	useEffect(() => {
		if (organizer) {
			setFormData({
				organizerName: organizer.organizer_name,
				representativeName: organizer.representative_name,
				email: organizer.email,
				phone: organizer.phone,
				website: organizer.website || "",
				description: organizer.description || "",
				logoUrl: organizer.logo_url || "https://via.placeholder.com/150",
			});
			const nextSocialLinks = createEmptySocialLinkState();
			(organizer.social_links || []).forEach((link) => {
				const platform = link.platform as SocialPlatformKey;
				if (platform in nextSocialLinks) {
					nextSocialLinks[platform] =
						link.input_value || link.handle || link.url || "";
				}
			});
			setSocialLinks(nextSocialLinks);
		} else {
			setSocialLinks(createEmptySocialLinkState());
		}
	}, [organizer]);

	// 入力フィールドの変更ハンドラ
	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	const handleSocialLinkChange = (
		platform: SocialPlatformKey,
		value: string
	) => {
		setSocialLinks((prev) => ({
			...prev,
			[platform]: value,
		}));
	};

	// URLのバリデーション
	const validateUrl = (url: string): boolean => {
		if (!url) return true; // 空の場合はOK
		try {
			new URL(url);
			return true;
		} catch (e) {
			return false;
		}
	};

	// フォーム送信ハンドラ
	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		// バリデーション
		if (!formData.organizerName.trim()) {
			toast.error("主催者名は必須です");
			return;
		}

		if (!formData.representativeName.trim()) {
			toast.error("代表者名は必須です");
			return;
		}

		if (!formData.email.trim()) {
			toast.error("メールアドレスは必須です");
			return;
		}

		if (!formData.phone.trim()) {
			toast.error("電話番号は必須です");
			return;
		}

		if (!validateUrl(formData.website)) {
			toast.error("ウェブサイトのURLが正しくありません");
			return;
		}

		setIsSubmitting(true);

		const formattedSocialLinks = Object.entries(socialLinks)
			.filter(([, value]) => value.trim() !== "")
			.map(([platform, value]) => ({
				platform,
				value: value.trim(),
			}));

		try {
			// updateOrganizerProfileを使用してプロフィールを更新
			// バックエンドAPIの仕様に合わせて適切なキー名に変換
			const success = await updateOrganizerProfile({
				organizerName: formData.organizerName,
				representativeName: formData.representativeName,
				email: formData.email,
				phone: formData.phone,
				website: formData.website,
				description: formData.description,
				socialLinks: formattedSocialLinks,
			});

			if (success) {
				// 最新データを取得
				await refreshOrganizerData();
				setIsEditing(false);
				toast.success("プロフィールを更新しました");
			} else {
				toast.error("プロフィールの更新に失敗しました");
			}
		} catch (error) {
			console.error("プロフィール更新エラー:", error);
			toast.error("プロフィールの更新に失敗しました");
		} finally {
			setIsSubmitting(false);
		}
	};

	// ロゴ画像の選択
	const handleLogoSelect = () => {
		if (fileInputRef.current) {
			fileInputRef.current.click();
		}
	};

	// ロゴ画像のアップロード
	const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		// ファイルのバリデーション
		if (!file.type.startsWith("image/")) {
			toast.error("画像ファイルを選択してください");
			return;
		}

		if (file.size > 5 * 1024 * 1024) {
			// 5MB
			toast.error("ファイルサイズは5MB以下にしてください");
			return;
		}

		setIsUploadingLogo(true);

		try {
			// プレビューを表示
			const reader = new FileReader();
			reader.onload = (e) => {
				if (e.target?.result) {
					setFormData({
						...formData,
						logoUrl: e.target.result as string,
					});
				}
			};
			reader.readAsDataURL(file);

			// FormDataを使用してアップロード
			const formDataUpload = new FormData();
			formDataUpload.append("logo", file);

			// APIリクエスト
			const response = await apiClient.post("/organizer/logo", formDataUpload, {
				headers: { "Content-Type": "multipart/form-data" },
			});

			// Backend returns logoUrl when upload succeeds
			if (response.data && response.data.logoUrl) {
				// 最新データを取得
				await refreshOrganizerData();
				toast.success("ロゴをアップロードしました");
			} else {
				throw new Error("アップロードに失敗しました");
			}
		} catch (error) {
			console.error("ロゴアップロードエラー:", error);
			toast.error("ロゴのアップロードに失敗しました");

			// エラー時は元のロゴに戻す
			if (organizer?.logo_url) {
				setFormData({
					...formData,
					logoUrl: organizer.logo_url,
				});
			}
		} finally {
			setIsUploadingLogo(false);
		}
	};

	// 主催者データが存在しない場合の表示
	if (!organizer) {
		return (
			<div className="container mx-auto max-w-5xl p-6 min-h-screen">
				<div className="mb-8">
					<h2 className="text-2xl font-bold text-gray-900 dark:text-white">
						主催者プロフィール
					</h2>
				</div>
				<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 text-center">
					<p className="text-gray-600 dark:text-gray-400">
						主催者データが見つかりません
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="container mx-auto max-w-5xl p-6 min-h-screen">
			<div className="mb-8">
				<div className="flex justify-between items-center">
					<div>
						<h2 className="text-2xl font-bold text-gray-900 dark:text-white">
							主催者プロフィール
						</h2>
						<p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
							主催者の基本情報を管理できます
						</p>
					</div>
					{!isEditing && (
						<button
							onClick={() => setIsEditing(true)}
							className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-6 py-2 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
						>
							編集する
						</button>
					)}
				</div>
			</div>
			<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
				{isEditing ? (
					<form onSubmit={handleSubmit} className="space-y-6">
						<div className="flex flex-col md:flex-row md:space-x-6">
							<div className="md:w-1/3 mb-6 md:mb-0">
								<div className="flex flex-col items-center">
									<div className="relative">
										<img
											src={formData.logoUrl}
											alt="主催者ロゴ"
											className={`w-32 h-32 object-cover rounded-full mb-4 ${
												isUploadingLogo ? "opacity-50" : ""
											}`}
										/>
										{isUploadingLogo && (
											<div className="absolute inset-0 flex items-center justify-center">
												<svg
													className="animate-spin h-10 w-10 text-violet-500 dark:text-violet-400"
													xmlns="http://www.w3.org/2000/svg"
													fill="none"
													viewBox="0 0 24 24"
												>
													<circle
														className="opacity-25"
														cx="12"
														cy="12"
														r="10"
														stroke="currentColor"
														strokeWidth="4"
													></circle>
													<path
														className="opacity-75"
														fill="currentColor"
														d="M4 12a8 8 0 0116 0H4z"
													></path>
												</svg>
											</div>
										)}
									</div>
									<input
										type="file"
										ref={fileInputRef}
										onChange={handleLogoUpload}
										accept="image/*"
										className="hidden"
									/>
									<button
										type="button"
										disabled={isUploadingLogo}
										onClick={handleLogoSelect}
										className={`cursor-pointer bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 sm:px-4 py-2 rounded-lg text-sm ${
											isUploadingLogo
												? "opacity-50 cursor-not-allowed"
												: "hover:bg-gray-300 dark:hover:bg-gray-600"
										}`}
									>
										{isUploadingLogo ? "アップロード中..." : "画像を変更"}
									</button>
								</div>
							</div>
							<div className="md:w-2/3 space-y-6">
								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
										主催者名{" "}
										<span className="text-red-500 dark:text-red-400">*</span>
									</label>
									<input
										type="text"
										name="organizerName"
										value={formData.organizerName}
										onChange={handleChange}
										required
										className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 focus:outline-none"
									/>
								</div>
								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
										代表者名{" "}
										<span className="text-red-500 dark:text-red-400">*</span>
									</label>
									<input
										type="text"
										name="representativeName"
										value={formData.representativeName}
										onChange={handleChange}
										required
										className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 focus:outline-none"
									/>
								</div>
								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
										メールアドレス{" "}
										<span className="text-red-500 dark:text-red-400">*</span>
									</label>
									<input
										type="email"
										name="email"
										value={formData.email}
										onChange={handleChange}
										required
										className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 focus:outline-none"
									/>
									<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
										公開ページには表示されず、管理者のみ確認できます。
									</p>
								</div>
								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
										電話番号{" "}
										<span className="text-red-500 dark:text-red-400">*</span>
									</label>
									<input
										type="tel"
										name="phone"
										value={formData.phone}
										onChange={handleChange}
										required
										className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 focus:outline-none"
									/>
									<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
										公開ページには表示されず、管理者のみ確認できます。
									</p>
								</div>
								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
										ウェブサイト
									</label>
									<input
										type="url"
										name="website"
										value={formData.website}
										onChange={handleChange}
										className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 focus:outline-none"
										placeholder="https://"
									/>
								</div>
								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
										説明
									</label>
									<textarea
										name="description"
										value={formData.description}
										onChange={handleChange}
										rows={4}
										className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 focus:outline-none"
										placeholder="主催者の説明や活動内容などを入力してください"
									></textarea>
								</div>
								<div>
									<div className="flex items-center gap-2 mb-2">
										<span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
											公式SNS（任意）
										</span>
									</div>
									<p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
										アカウントID（@example など）または URL
										を入力すると、自動でリンクに変換されます。
									</p>
									<div className="grid gap-4 md:grid-cols-2">
										{SOCIAL_PLATFORMS.map((platform) => {
											const Icon = SOCIAL_PLATFORM_ICONS[platform.key];
											return (
												<div key={platform.key} className="space-y-1">
													<div className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
														{Icon && (
															<Icon className="w-4 h-4 mr-2 text-gray-500 dark:text-gray-400" />
														)}
														{platform.label}
													</div>
													<input
														type="text"
														value={socialLinks[platform.key]}
														onChange={(e) =>
															handleSocialLinkChange(
																platform.key,
																e.target.value
															)
														}
														placeholder={platform.placeholder}
														className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 focus:outline-none"
													/>
												</div>
											);
										})}
									</div>
								</div>
								<div className="flex justify-end space-x-3 pt-4">
									<button
										type="button"
										onClick={() => setIsEditing(false)}
										disabled={isSubmitting}
										className={`bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-6 py-2 rounded-lg ${
											isSubmitting
												? "opacity-50 cursor-not-allowed"
												: "hover:bg-gray-300 dark:hover:bg-gray-600"
										}`}
									>
										キャンセル
									</button>
									<button
										type="submit"
										disabled={isSubmitting}
										className={`bg-gradient-to-r from-violet-500 to-purple-600 text-white px-6 py-2 rounded-lg ${
											isSubmitting
												? "opacity-50 cursor-not-allowed"
												: "hover:from-violet-600 hover:to-purple-700"
										}`}
									>
										{isSubmitting ? "保存中..." : "保存する"}
									</button>
								</div>
							</div>
						</div>
					</form>
				) : (
					<div className="flex flex-col md:flex-row md:space-x-6">
						<div className="md:w-1/3 mb-6 md:mb-0">
							<div className="flex flex-col items-center">
								<img
									src={formData.logoUrl}
									alt="主催者ロゴ"
									className="w-32 h-32 object-cover rounded-full"
								/>
							</div>
						</div>
						<div className="md:w-2/3">
							<h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
								{formData.organizerName}
							</h3>
							<div className="space-y-4">
								<div>
									<div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
										代表者
									</div>
									<div className="text-gray-900 dark:text-white">
										{formData.representativeName}
									</div>
								</div>
								<div>
									<div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
										連絡先
									</div>
									<div className="text-gray-900 dark:text-white">
										{formData.email}
									</div>
									<div className="text-gray-900 dark:text-white">
										{formData.phone}
									</div>
									<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
										これらの連絡先は公開ページには表示されません。
									</p>
								</div>
								{formData.website && (
									<div>
										<div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
											ウェブサイト
										</div>
										<a
											href={formData.website}
											target="_blank"
											rel="noopener noreferrer"
											className="text-violet-600 dark:text-violet-400 hover:underline"
										>
											{formData.website}
										</a>
									</div>
								)}
								{formData.description && (
									<div>
										<div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
											プロフィール
										</div>
										<p className="text-gray-900 dark:text-white whitespace-pre-wrap">
											{formData.description}
										</p>
									</div>
								)}
								{socialLinkBadges.length > 0 && (
									<div>
										<div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
											SNS
										</div>
										<div className="flex flex-wrap gap-2">
											{socialLinkBadges.map(({ key, Icon, label, url }) => (
												<a
													key={key}
													href={url}
													target="_blank"
													rel="noopener noreferrer"
													className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 dark:bg-gray-700 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
												>
													{Icon && <Icon className="w-4 h-4" />}
													<span>{label}</span>
												</a>
											))}
										</div>
									</div>
								)}
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default ProfileManagement;
